import {useMemo} from 'react';

import {
  useEntityMemberRoleQuery,
  useProjectMemberQuery,
} from '../generated/graphql';
import {useViewer} from '../state/viewer/hooks';
import {useAdminModeActive} from './admin';

export enum EntityProjectPermissions {
  DeleteProject = 'project:delete',
  RenameProject = 'project:rename',
  UpdateProject = 'project:update',
  CreateRun = 'run:create',
  DeleteRun = 'run:delete',
  MoveRun = 'run:move',
  StopRun = 'run:stop',
  UndeleteRun = 'run:undelete',
  UpdateRun = 'run:update',
}

/**
 * Fetches the viewer's full permission set for an (entity, project) with a single
 * pair of queries and returns a `getHasPermission(permission)` checker. The
 * queries do not depend on any individual permission, so components that need to
 * check several permissions should call this once and check strings in memory
 * rather than firing a separate query per permission.
 *
 * Pass `skip` to avoid issuing the queries entirely (e.g. when the result is only
 * needed for a read-only project); the checker then reports false for everything.
 */
export function useViewerPermissionsForEntityProject(
  projectName: string,
  entityName: string,
  skip = false
): {getHasPermission: (permission: string) => boolean} {
  const viewer = useViewer();
  const isAdminModeActive = useAdminModeActive();

  const {data: projectData} = useProjectMemberQuery({
    variables: {
      entityName,
      projectName,
      memberId: viewer?.id ?? '',
    },
    skip: skip || !projectName || !entityName || !viewer,
  });

  const {data: entityData} = useEntityMemberRoleQuery({
    variables: {entityName, memberId: viewer?.id ?? ''},
    skip: skip || !viewer || !entityName,
    fetchPolicy: 'cache-and-network',
  });

  const projectMemberRole = projectData?.project?.member?.role;
  const entityMemberRole = entityData?.entity?.member?.memberRole;

  return useMemo(() => {
    if (skip) {
      return {getHasPermission: () => false};
    }
    if (isAdminModeActive) {
      return {getHasPermission: () => true};
    }
    // Use the viewer's project role if it exists, otherwise their entity role —
    // matching the original single-permission resolution.
    if (projectMemberRole) {
      const permissionNames = new Set(
        (projectMemberRole.inheritedFrom?.permissions ?? [])
          .concat(projectMemberRole.permissions ?? [])
          .map(p => p.name)
      );
      return {
        getHasPermission: (permission: string) =>
          permissionNames.has(permission),
      };
    }
    const permissionNames = new Set(
      (entityMemberRole?.inheritedFrom?.permissions ?? [])
        .concat(entityMemberRole?.permissions ?? [])
        .map(p => p.name)
    );
    return {
      getHasPermission: (permission: string) => permissionNames.has(permission),
    };
  }, [skip, isAdminModeActive, projectMemberRole, entityMemberRole]);
}
